import { maybe, Maybe } from '@passionware/monads';

/**
 * Cache mechanism that requires a factory to depend only on the key, with weak references to values for object keys.
 */
export function createWeakCache<
  K extends object | Maybe<string>,
  V extends object
>(factory: (key: K) => V) {
  const weakCache = new WeakMap<object, WeakRef<V>>();
  const strongCache = new Map<Maybe<string>, WeakRef<V>>();
  // @ts-expect-error -- key can be Maybe<string>
  const isMaybeString = (key: K): key is Maybe<string> =>
    typeof key === 'string' || maybe.isAbsent(key);
  const cleanupRegistry = new FinalizationRegistry<K>(key => {
    if (isMaybeString(key)) {
      strongCache.delete(key);
    } else {
      weakCache.delete(key);
    }
  });

  function getRef(key: K): WeakRef<V> | undefined {
    return isMaybeString(key) ? strongCache.get(key) : weakCache.get(key);
  }

  function setRef(key: K, ref: WeakRef<V>) {
    if (isMaybeString(key)) {
      strongCache.set(key, ref);
    } else {
      weakCache.set(key, ref);
    }
  }

  return {
    get: (key: K): V | undefined => {
      const ref = getRef(key);
      return ref ? ref.deref() : undefined;
    },
    set: (key: K, value: V) => {
      const ref = new WeakRef(value);
      setRef(key, ref);
      cleanupRegistry.register(value, key);
    },
    has: (key: K): boolean => {
      const ref = getRef(key);
      return !!(ref && ref.deref() !== undefined);
    },
    delete: (key: K) => {
      const ref = getRef(key);
      if (ref) {
        const value = ref.deref();
        if (value) {
          cleanupRegistry.unregister(value);
        }
      }
      return isMaybeString(key)
        ? strongCache.delete(key)
        : weakCache.delete(key);
    },
    getOrCreate: (key: K): V => {
      let ref = getRef(key);
      if (!ref || !ref.deref()) {
        // console.log('Cache miss');
        const value = factory(key);
        ref = new WeakRef(value);
        setRef(key, ref);
        cleanupRegistry.register(value, key);
      }
      // console.log('Cache hit');
      return ref.deref()!;
    }
  };
}
